import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Filter, Activity, Table, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

export interface ProfileRunFilterChip {
  id: string;
  type: 'state' | 'sourceTable';
  value: string;
  label: string;
}

interface ProfileRunFiltersProps {
  sourceTables: { id: string; name: string }[];
  filters: ProfileRunFilterChip[];
  onFiltersChange: (filters: ProfileRunFilterChip[]) => void;
}

/**
 * Component for managing profile-specific run filters (state and source table only)
 */
export function ProfileRunFilters({ sourceTables, filters, onFiltersChange }: ProfileRunFiltersProps) {
  const { t } = useLanguage();
  const [customSourceTable, setCustomSourceTable] = useState('');

  // Get run states with translations
  const RUN_STATES = [
    { value: 'Failed', label: t.runs.runStates.Failed },
    { value: 'Completed', label: t.runs.runStates.Completed },
    { value: 'Extracting', label: t.runs.runStates.Extracting },
    { value: 'Loading', label: t.runs.runStates.Loading },
    { value: 'Scheduled', label: t.runs.runStates.Scheduled },
    { value: 'Skipped', label: t.runs.runStates.Skipped },
    { value: 'Cancelled', label: t.runs.runStates.Cancelled },
    { value: 'WaitingForTargetLease', label: t.runs.runStates.WaitingForTargetLease },
    { value: 'WaitingForSourceCapacity', label: t.runs.runStates.WaitingForSourceCapacity },
  ];

  /**
   * Toggles a state filter
   */
  const handleToggleStateFilter = (state: string) => {
    const existingFilter = filters.find(f => f.type === 'state' && f.value === state);
    if (existingFilter) {
      onFiltersChange(filters.filter(f => f.id !== existingFilter.id));
    } else {
      const stateLabel = RUN_STATES.find(s => s.value === state)?.label ?? state;
      const newFilter: ProfileRunFilterChip = {
        id: `state-${state}`,
        type: 'state',
        value: state,
        label: stateLabel,
      };
      onFiltersChange([...filters, newFilter]);
    }
  };

  /**
   * Toggles a source table filter (using logical name)
   */
  const handleToggleSourceTableFilter = (sourceTableName: string) => {
    const existingFilter = filters.find(f => f.type === 'sourceTable' && f.value === sourceTableName);
    if (existingFilter) {
      onFiltersChange(filters.filter(f => f.id !== existingFilter.id));
    } else {
      const newFilter: ProfileRunFilterChip = {
        id: `sourceTable-${sourceTableName}`,
        type: 'sourceTable',
        value: sourceTableName,
        label: sourceTableName,
      };
      onFiltersChange([...filters, newFilter]);
    }
  };

  /**
   * Adds a custom source table filter
   */
  const handleAddCustomSourceTable = () => {
    const trimmed = customSourceTable.trim();
    if (!trimmed) return;

    // Check if filter already exists
    const existingFilter = filters.find(f => f.type === 'sourceTable' && f.value === trimmed);
    if (existingFilter) {
      setCustomSourceTable('');
      return;
    }

    const newFilter: ProfileRunFilterChip = {
      id: `sourceTable-${trimmed}`,
      type: 'sourceTable',
      value: trimmed,
      label: trimmed,
    };
    onFiltersChange([...filters, newFilter]);
    setCustomSourceTable('');
  };

  /**
   * Clears all filters
   */
  const handleClearAll = () => {
    onFiltersChange([]);
  };

  const hasActiveFilters = filters.length > 0;

  return (
    <div className="space-y-4">
      {/* Filter Dropdown */}
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                hasActiveFilters && "border-primary bg-primary/10 text-primary hover:bg-primary/20"
              )}
            >
              <Filter className={cn("h-4 w-4 mr-2", hasActiveFilters && "fill-current")} />
              {t.allRuns.filters.filterButton}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {/* Clear All Button */}
            {hasActiveFilters && (
              <>
                <DropdownMenuItem onClick={handleClearAll}>
                  {t.allRuns.filters.clearAll}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {/* Run State Submenu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Activity className="h-4 w-4 mr-2" />
                {t.allRuns.filters.runState}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56">
                {RUN_STATES.map(state => (
                  <DropdownMenuCheckboxItem
                    key={state.value}
                    checked={filters.some(f => f.type === 'state' && f.value === state.value)}
                    onCheckedChange={() => { handleToggleStateFilter(state.value); }}
                  >
                    {state.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Source Table Submenu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Table className="h-4 w-4 mr-2" />
                {t.allRuns.filters.sourceTable}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-auto min-w-fit">
                {/* Custom source table input */}
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <Input
                    placeholder={t.allRuns.filters.customSourceTable}
                    value={customSourceTable}
                    onChange={(e) => { setCustomSourceTable(e.target.value); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomSourceTable();
                      }
                      e.stopPropagation();
                    }}
                    className="h-8 text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAddCustomSourceTable}
                    disabled={!customSourceTable.trim()}
                    className="h-8 px-2"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {/* Show all source tables and any custom filters not in the list */}
                {(() => {
                  const sourceTableNames = new Set(sourceTables.map(t => t.name));
                  const customFilters = filters.filter(
                    f => f.type === 'sourceTable' && !sourceTableNames.has(f.value)
                  );
                  const allItems = [
                    ...sourceTables.map(t => t.name),
                    ...customFilters.map(f => f.value),
                  ];

                  if (allItems.length === 0) return null;

                  return (
                    <>
                      <DropdownMenuSeparator />
                      {allItems.map(name => (
                        <DropdownMenuCheckboxItem
                          key={name}
                          checked={filters.some(f => f.type === 'sourceTable' && f.value === name)}
                          onCheckedChange={() => { handleToggleSourceTableFilter(name); }}
                          className="whitespace-nowrap"
                        >
                          {name}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </>
                  );
                })()}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
